/**
 * Authentic Egyptian domino terminology (table slang).
 *
 * Source of truth for tile names as called in Cairo ahwas. The two big
 * doubles mix-ups are fixed here and everywhere the UI labels tiles:
 *   - 5-5 is "Dabsh" (دبش)  — NOT "Dorgy"
 *   - 4-4 is "Dargi" (درجي) — the joker-sounding one, sometimes "Giorgi"
 * Numbers come from the Persian-derived backgammon lineage: abiyad, yek, do,
 * seh, gahar, besh, sheesh.
 */

export interface TileName {
  /** Latin transliteration, e.g. "Sheesh Besh". */
  en: string;
  /** Arabic script, e.g. "شيش بيش". */
  ar: string;
}

/** Names of the pips 0..6 (أسماء الأرقام). */
export const PIP_NAMES: readonly string[] = [
  "Abyad",
  "Yek",
  "Do",
  "Seh",
  "Gahar",
  "Besh",
  "Sheesh",
];

export const PIP_NAMES_AR: readonly string[] = [
  "أبيض",
  "يك",
  "دو",
  "سه",
  "جهار",
  "بيش",
  "شيش",
];

/** The seven doubles (الدبلات) — corrected authentic names. */
export const DOUBLE_NAMES: Readonly<Record<number, TileName>> = {
  0: { en: "Balata", ar: "بلاطة" },
  1: { en: "Habeyk", ar: "هابيك" },
  2: { en: "Dobara", ar: "دوبارة" },
  3: { en: "Dosa", ar: "دوسة" },
  4: { en: "Dargi", ar: "درجي" },
  5: { en: "Dabsh", ar: "دبش" },
  6: { en: "Dosh", ar: "دش" },
};

/** Short flavour note per double, shown in callouts and the guide. */
export const DOUBLE_NOTES: Readonly<Record<number, string>> = {
  6: "The king. Holding Dosh opens round one of the match.",
  5: "Second highest. Slam it loud — the word Dabsh gave us el-Dabsha.",
  4: "Dargi — the turner. Watch who it traps.",
  3: "Middle child of the set.",
  2: "Cheap to hold, cheap to lose.",
  1: "Light stone for a safe exit.",
  0: "The blank. Counts nothing at the count-up.",
};

/** Mixed tiles worth calling out on the table. */
const FAMOUS_MIXED: Readonly<Record<string, string>> = {
  "6-5": "Sheesh Besh",
  "6-4": "Sheesh Gahar",
  "6-3": "Sheesh Seh",
  "6-2": "Sheesh Do",
  "6-1": "Sheesh Yek",
  "6-0": "Sheesh Abyad",
  "5-4": "Besh Gahar",
  "5-0": "Besh Abyad",
  "4-2": "Gahar Do",
  "4-0": "Gahar Abyad",
};

/**
 * Name for any tile. High number is always called first, as at the table.
 * Doubles get their double name; famous mixed pairs get their callout;
 * everything else is "Sheesh Do" style.
 */
export function tileName(high: number, low: number): TileName {
  if (high === low) {
    const d = DOUBLE_NAMES[high] ?? { en: `${high}-${low}`, ar: `${high}-${low}` };
    return { ...d };
  }
  const famous = FAMOUS_MIXED[`${high}-${low}`];
  if (famous) return { en: famous, ar: famousAr(famous) };
  const hi = PIP_NAMES[high] ?? String(high);
  const lo = PIP_NAMES[low] ?? String(low);
  return {
    en: `${hi} ${lo}`,
    ar: `${PIP_NAMES_AR[high] ?? high} ${PIP_NAMES_AR[low] ?? low}`,
  };
}

function famousAr(en: string): string {
  const map: Record<string, string> = {
    "Sheesh Besh": "شيش بيش",
    "Sheesh Gahar": "شيش جهار",
    "Sheesh Seh": "شيش سه",
    "Sheesh Do": "شيش دو",
    "Sheesh Yek": "شيش يك",
    "Sheesh Abyad": "شيش أبيض",
    "Besh Gahar": "بيش جهار",
    "Besh Abyad": "بيش أبيض",
    "Gahar Do": "جهار دو",
    "Gahar Abyad": "جهار أبيض",
  };
  return map[en] ?? en;
}

/** Other ahwa vocabulary the UI uses. */
export const TABLE_TERMS = {
  game: { en: "Domino", ar: "دومينو" },
  tile: { en: "El-Hagar", ar: "الحجر" },
  hand: { en: "El-Kaf", ar: "الكف" },
  boneyard: { en: "El-Mashoob", ar: "المشحوب" },
  block: { en: "Qafla", ar: "قفلة" },
  trap: { en: "Habsa", ar: "حبسة" },
  slam: { en: "El-Dabsha", ar: "الدبشة" },
  yourTurn: { en: "Dorak", ar: "دورك" },
} as const;
